import type { CSSProperties } from 'react';

/**
 * Avatar color palette. Pulled from the design-spec seed users (data.jsx) —
 * six distinct hues that read well on both the light Trello chrome and the
 * colored ticket-modal cover strip. Names are hashed into this palette so a
 * given user gets a stable color across surfaces.
 */
const AVATAR_PALETTE = [
  '#0b6fc2', // blue
  '#8a3ffc', // violet
  '#d8480f', // burnt orange
  '#0f8a5f', // green
  '#b7791f', // amber
  '#a7185b', // magenta
  '#0f6e7a', // teal
  '#5d4037', // brown
];

/**
 * Trello-style neutral gray for the unassigned / empty default state. This
 * is intentionally distinct from the AVATAR_PALETTE so a missing assignee
 * doesn't read as "someone is assigned". Matches the existing
 * `--ft-archived-accent` gray (#97a0af) we already use for muted chips.
 *
 * TODO: tokenize once we add a dedicated `--ft-neutral-200` / `--ft-gray-*`
 * scale to design-tokens.css. Until then, keep this as a literal so the
 * primitive avatar doesn't depend on a CSS variable that may not be loaded
 * (Avatar is rendered inside email-style strings on some surfaces).
 */
const UNASSIGNED_AVATAR_BG = '#97a0af';

/**
 * True when the supplied name should be treated as "no assignee". Covers
 * empty/null/whitespace and the literal "Unassigned" (case-insensitive)
 * that we surface as the assignee fallback today.
 */
function isUnassigned(name: string | null | undefined): boolean {
  if (!name) return true;
  return name.trim().toLowerCase() === 'unassigned';
}

/** Stable djb2-style hash → palette index. Empty names fall back to index 0. */
function hashIndex(name: string, mod: number): number {
  if (!name) return 0;
  let h = 5381;
  for (let i = 0; i < name.length; i += 1) {
    h = ((h << 5) + h + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % mod;
}

/**
 * Pull up to two initials from a human name ("Jane Doe" → "JD", "Alex" → "A").
 *
 * Drops bracketed/parenthesized tokens entirely (so "(test)", "[admin]" etc.
 * don't contribute initials), then strips remaining non-letter chars from
 * each surviving token before taking its first character. This yields "DA"
 * for "Dev Agent (test)" rather than "DT" — completes F-001 (slice K).
 *
 * Sanity:
 *   "Dev Agent (test)"  → "DA"
 *   "Bob"               → "B"
 *   "Bob Smith"         → "BS"
 *   "Bob Smith (admin)" → "BS"
 *   "(test)"            → "T"   (no other tokens — fall back rather than "?")
 *   ""/null/undefined   → "?"
 */
export function initialsFrom(name: string): string {
  if (!name) return '?';
  // Use Unicode letter class so accented names ("José Núñez") survive intact;
  // \p{L} matches any kind of letter from any language.
  const tokens = name.split(/\s+/).filter(Boolean);
  const isBracketed = (tok: string) =>
    /^[(\[{<].*[)\]}>]$/.test(tok) || /^[(\[{<]/.test(tok) || /[)\]}>]$/.test(tok);
  // First pass: prefer non-bracketed tokens. Fall back to all tokens if every
  // word is bracketed (e.g. raw "(test)" — keep "T" rather than "?").
  const cleaned = (toks: string[]) =>
    toks.map((t) => t.replace(/[^\p{L}]/gu, '')).filter(Boolean);
  let letters = cleaned(tokens.filter((t) => !isBracketed(t)));
  if (letters.length === 0) letters = cleaned(tokens);
  if (letters.length === 0) return '?';
  const first = letters[0][0] ?? '';
  const last = letters.length > 1 ? letters[letters.length - 1][0] ?? '' : '';
  const out = (first + last).toUpperCase();
  return out || '?';
}

export interface AvatarProps {
  name: string;
  /** Pixel size of the circle. Default 24. */
  size?: number;
  className?: string;
  /** Native tooltip. If omitted, falls back to `name`. */
  title?: string;
  /** Overrides the hashed color — useful when the caller has a canonical
   *  user tint (e.g. from `users.color` in a seeded dataset). */
  color?: string;
}

/**
 * Circular initials avatar. Produces a stable palette color per-name, white
 * 2px outer ring for stacking against colored backgrounds, and Inter 600
 * centered initials.
 */
export function Avatar({
  name,
  size = 24,
  className,
  title,
  color,
}: AvatarProps) {
  // Unassigned / empty names render as neutral gray so they don't visually
  // imply a real owner. An explicit `color` prop still wins — callers that
  // pass a canonical user tint shouldn't be overridden.
  const background =
    color ??
    (isUnassigned(name)
      ? UNASSIGNED_AVATAR_BG
      : AVATAR_PALETTE[hashIndex(name, AVATAR_PALETTE.length)]);
  const initials = initialsFrom(name);
  // Font size scales with the circle — ~40% of the diameter keeps two-letter
  // initials inside a 24px bubble without touching the edges.
  const fontSize = Math.max(10, Math.round(size * 0.42));

  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    background,
    color: '#ffffff',
    fontSize,
    fontWeight: 600,
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    lineHeight: 1,
    letterSpacing: 0.2,
    boxShadow: '0 0 0 2px var(--ft-surface, #fff)',
    userSelect: 'none',
  };

  return (
    <span
      className={className}
      style={style}
      title={title ?? name}
      aria-label={title ?? name}
    >
      {initials}
    </span>
  );
}
